import React, { useMemo } from 'react';

const CHART_COLORS = [
  '#14b8a6',
  '#38bdf8',
  '#a78bfa',
  '#fbbf24',
  '#f472b6',
  '#34d399',
  '#fb7185',
  '#60a5fa',
];

/** Strip **bold** / _italic_ / `code` markers the model sometimes leaves in labels. */
function stripInlineMarkdown(text) {
  let s = String(text || '').trim();
  if (!s) return '';
  for (let i = 0; i < 3; i += 1) {
    const next = s
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/_(.+?)_/g, '$1')
      .replace(/`(.+?)`/g, '$1')
      .replace(/~~(.+?)~~/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    if (next === s) break;
    s = next;
  }
  return s.replace(/^\*+|\*+$/g, '').replace(/^_+|_+$/g, '').trim();
}

export function normalizeVizSpec(input) {
  const d = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const allowed = new Set(['bar', 'horizontal_bar', 'line', 'area', 'donut']);
  const chartType = allowed.has(d.chartType) ? d.chartType : 'bar';
  const series = (Array.isArray(d.series) ? d.series : [])
    .map((row) => {
      const label = stripInlineMarkdown(row?.label ?? row?.x ?? row?.name ?? '');
      const raw = row?.value ?? row?.y ?? row?.count;
      const value = typeof raw === 'number'
        ? raw
        : parseFloat(stripInlineMarkdown(String(raw ?? '')).replace(/,/g, ''));
      if (!label || !Number.isFinite(value)) return null;
      return { label: label.slice(0, 80), value };
    })
    .filter(Boolean)
    .slice(0, 50);
  return {
    chartType,
    title: stripInlineMarkdown(d.title || 'Visualization').slice(0, 200) || 'Visualization',
    subtitle: stripInlineMarkdown(d.subtitle || '').slice(0, 300),
    xLabel: stripInlineMarkdown(d.xLabel || '').slice(0, 80),
    yLabel: stripInlineMarkdown(d.yLabel || '').slice(0, 80),
    series,
    note: String(d.note || '').trim().slice(0, 2000),
    createdByAi: !!d.createdByAi,
  };
}

function BarChart({ series, horizontal = false }) {
  const max = Math.max(...series.map((s) => s.value), 1);
  if (horizontal) {
    return (
      <div className="space-y-2.5">
        {series.map((item, idx) => {
          const pct = Math.max(2, Math.round((item.value / max) * 100));
          const color = CHART_COLORS[idx % CHART_COLORS.length];
          return (
            <div key={`${item.label}-${idx}`} className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)_auto] gap-2 items-center">
              <div className="text-[12px] text-slate-300 truncate" title={item.label}>{item.label}</div>
              <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
              </div>
              <div className="text-[12px] font-medium tabular-nums text-slate-200 text-right min-w-[2.5rem]">
                {Number.isInteger(item.value) ? item.value.toLocaleString() : item.value}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const height = 160;
  const pad = { t: 12, r: 8, b: 36, l: 8 };
  const width = Math.max(280, series.length * 48);
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const barW = Math.min(36, Math.max(12, (innerW / series.length) * 0.65));

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} className="mx-auto block">
        <line x1={pad.l} y1={pad.t + innerH} x2={width - pad.r} y2={pad.t + innerH} stroke="#334155" strokeWidth="1" />
        {series.map((item, idx) => {
          const h = (item.value / max) * innerH;
          const cx = pad.l + (innerW / series.length) * (idx + 0.5);
          const x = cx - barW / 2;
          const y = pad.t + innerH - h;
          return (
            <g key={`${item.label}-${idx}`}>
              <rect x={x} y={y} width={barW} height={Math.max(h, 1)} rx="3" fill={CHART_COLORS[idx % CHART_COLORS.length]} />
              <text x={cx} y={height - 10} textAnchor="middle" fontSize="10" fill="#94a3b8">
                {item.label.length > 8 ? `${item.label.slice(0, 7)}…` : item.label}
              </text>
              <title>{`${item.label}: ${item.value}`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function LineAreaChart({ series, area = false }) {
  const width = 560;
  const height = 180;
  const pad = 28;
  const vals = series.map((s) => s.value);
  const maxV = Math.max(...vals, 1);
  const minV = Math.min(...vals, 0);
  const span = Math.max(maxV - minV, 1);
  const n = series.length;
  const coords = series.map((p, i) => {
    const x = pad + ((width - pad * 2) * (n === 1 ? 0.5 : i / (n - 1)));
    const y = height - pad - ((p.value - minV) / span) * (height - pad * 2);
    return { x, y, ...p };
  });
  const poly = coords.map((p) => `${p.x},${p.y}`).join(' ');
  const areaPath = coords.length
    ? `M ${coords[0].x},${height - pad} L ${poly.replace(/ /g, ' L ')} L ${coords[coords.length - 1].x},${height - pad} Z`
    : '';

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} className="mx-auto block">
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#334155" strokeWidth="1" />
        {area && areaPath && <path d={areaPath} fill="rgba(52,211,153,0.15)" />}
        <polyline fill="none" stroke="#34d399" strokeWidth="2" points={poly} strokeLinejoin="round" strokeLinecap="round" />
        {coords.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3.5} fill="#6ee7b7" stroke="#059669">
            <title>{`${p.label}: ${p.value}`}</title>
          </circle>
        ))}
        <text x={4} y={pad} fontSize="10" fill="#64748b">{Number.isInteger(maxV) ? maxV : maxV.toFixed(1)}</text>
        <text x={4} y={height - 4} fontSize="10" fill="#64748b">{Number.isInteger(minV) ? minV : minV.toFixed(1)}</text>
      </svg>
      <div className="flex flex-wrap gap-2 justify-center mt-1">
        {series.map((s, i) => (
          <span key={i} className="text-[10px] text-slate-500">{s.label}</span>
        ))}
      </div>
    </div>
  );
}

function DonutChart({ series }) {
  const positive = series.filter((s) => s.value > 0);
  if (positive.length < 1) return null;
  const total = positive.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return null;

  const size = 140;
  const stroke = 18;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="flex items-center gap-4 flex-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0 mx-auto">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {positive.map((item, idx) => {
            const len = (item.value / total) * c;
            const dashOffset = -offset;
            offset += len;
            return (
              <circle
                key={`${item.label}-arc-${idx}`}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                strokeWidth={stroke}
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={dashOffset}
              >
                <title>{`${item.label}: ${item.value}`}</title>
              </circle>
            );
          })}
        </g>
        <text x="50%" y="48%" textAnchor="middle" fill="#e2e8f0" style={{ fontSize: 16, fontWeight: 700 }}>
          {Number.isInteger(total) ? total.toLocaleString() : total.toFixed(1)}
        </text>
        <text x="50%" y="62%" textAnchor="middle" fill="#64748b" style={{ fontSize: 9 }}>total</text>
      </svg>
      <ul className="space-y-1.5 min-w-0 flex-1">
        {positive.slice(0, 12).map((item, idx) => (
          <li key={`${item.label}-leg-${idx}`} className="flex items-center gap-2 text-[12px] min-w-0">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: CHART_COLORS[idx % CHART_COLORS.length] }} />
            <span className="truncate text-slate-200">{item.label}</span>
            <span className="ml-auto tabular-nums text-slate-400">
              {item.value.toLocaleString()} · {Math.round((item.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Interactive chart for AI-created Artifacts (and Companion chat previews).
 */
export default function ArtifactViz({ data, compact = false, className = '' }) {
  const spec = useMemo(() => normalizeVizSpec(data), [data]);
  const { chartType, title, subtitle, xLabel, yLabel, series, note } = spec;

  if (!series.length) {
    return (
      <div className={`rounded-xl border border-slate-700/80 bg-slate-900/50 px-4 py-8 text-center text-xs text-slate-500 ${className}`}>
        No series data yet. Ask AI Companion to build a visualization.
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-slate-700/80 bg-slate-900/50 ${compact ? 'p-3' : 'p-4'} ${className}`}>
      <div className="mb-3">
        <div className="flex items-start justify-between gap-2">
          <h4 className={`font-semibold text-slate-100 ${compact ? 'text-sm' : 'text-base'}`}>{title}</h4>
          {spec.createdByAi && (
            <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-teal-600/40 text-teal-300 bg-teal-600/10">
              AI
            </span>
          )}
        </div>
        {subtitle ? <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p> : null}
        {(xLabel || yLabel) && (
          <p className="text-[10px] text-slate-500 mt-1">
            {[xLabel && `X: ${xLabel}`, yLabel && `Y: ${yLabel}`].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      {chartType === 'donut' && <DonutChart series={series} />}
      {(chartType === 'line' || chartType === 'area') && <LineAreaChart series={series} area={chartType === 'area'} />}
      {(chartType === 'bar' || chartType === 'horizontal_bar') && (
        <BarChart series={series} horizontal={chartType === 'horizontal_bar'} />
      )}
      {note ? <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">{note}</p> : null}
    </div>
  );
}
